import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  FileText, 
  ShoppingBag, 
  LogIn
} from 'lucide-react';
import { PermissionLevel } from '../../types';
import { cn } from '../../utils/cn';

interface PublicUser {
  id: string;
  email: string;
  role: PermissionLevel;
  name?: string;
  avatar_url?: string;
}

interface PublicMobileTabBarProps {
  user?: PublicUser | null;
  isAuthenticated?: boolean;
  currentView?: 'files' | 'profile' | 'order';
  onViewChange?: (view: 'files' | 'profile' | 'order') => void;
  onOrderQuery?: (orderNumber: string) => void;
}

const PublicMobileTabBar: React.FC<PublicMobileTabBarProps> = ({ 
  user, 
  isAuthenticated,
  currentView = 'files',
  onViewChange,
  onOrderQuery
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useParams<{ tenant: string }>();
  
  // 获取当前租户，默认 'cn'
  const currentTenant = (tenant === 'cn' || tenant === 'intl') ? tenant : 'cn';

  const handleOrderQuery = () => {
    if (onViewChange) {
      onViewChange('order');
    }
  };


  return (
    <>
      {/* 底部导航栏 - Apple 风格增强 */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl border-t border-gray-200/50 dark:border-white/10 pb-[env(safe-area-inset-bottom)]">
        <div className="h-16 flex items-center justify-around px-2 select-none">
          {/* 文件系统 */}
          <button
            onClick={() => {
              if (onViewChange) {
                onViewChange('files');
              } else {
                navigate('/');
              }
            }}
            className={cn(
              "flex flex-col items-center justify-center h-full flex-1 transition-all duration-300 relative",
              currentView === 'files'
                ? 'text-teal-600 dark:text-teal-400' 
                : 'text-gray-400 hover:text-gray-600 dark:text-gray-500'
            )}
          >
            <div className={cn(
              "p-1.5 rounded-xl transition-all duration-300",
              currentView === 'files' && "bg-teal-50 dark:bg-teal-500/10 scale-110"
            )}>
              <FileText 
                size={22} 
                strokeWidth={currentView === 'files' ? 2.5 : 2} 
              />
            </div>
            <span className={cn(
              "text-[10px] mt-1 font-medium transition-all",
              currentView === 'files' ? "opacity-100 scale-100" : "opacity-70 scale-95"
            )}>文件系统</span>
          </button>
          
          {/* 中间主要功能：通常放订单查询或搜索 */}
          {/* 订单查询 */}
          <button
            onClick={handleOrderQuery}
            className={cn(
              "flex flex-col items-center justify-center h-full flex-1 transition-all duration-300 relative",
              currentView === 'order'
                ? 'text-orange-600 dark:text-orange-400' 
                : 'text-gray-400 hover:text-gray-600 dark:text-gray-500'
            )}
          >
            <div className={cn(
              "p-1.5 rounded-xl transition-all duration-300 relative",
              currentView === 'order' && "bg-orange-50 dark:bg-orange-500/10 scale-110"
            )}>
              <ShoppingBag 
                size={22} 
                strokeWidth={currentView === 'order' ? 2.5 : 2} 
              />
              {/* 可选：小红点或徽章 */}
            </div>
            <span className={cn(
              "text-[10px] mt-1 font-medium transition-all",
              currentView === 'order' ? "opacity-100 scale-100" : "opacity-70 scale-95"
            )}>订单查询</span>
          </button>

          {/* 个人中心 */}
          <button
            onClick={() => {
              if (isAuthenticated && user) {
                if (onViewChange) {
                  onViewChange('profile');
                } else {
                  navigate('/personal-center');
                }
              } else {
                navigate(`/${currentTenant}/login`, { state: { from: location.pathname } });
              }
            }}
            className={cn(
              "flex flex-col items-center justify-center h-full flex-1 transition-all duration-300 relative",
              currentView === 'profile' && isAuthenticated
                ? 'text-teal-600 dark:text-teal-400' 
                : 'text-gray-400 hover:text-gray-600 dark:text-gray-500'
            )}
          >
            {isAuthenticated && user ? (
              <>
                <div className={cn(
                  "p-0.5 rounded-full border-2 transition-all duration-300",
                  currentView === 'profile' 
                    ? "border-teal-500 scale-110" 
                    : "border-transparent"
                )}>
                  {user.avatar_url ? (
                    <img 
                      src={user.avatar_url} 
                      alt={user.name || user.email}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-[10px] font-bold">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <span className={cn(
                  "text-[10px] mt-1 font-medium transition-all",
                  currentView === 'profile' ? "opacity-100 scale-100" : "opacity-70 scale-95"
                )}>个人中心</span>
              </>
            ) : (
              <>
                <div className={cn(
                  "p-1.5 rounded-xl transition-all duration-300",
                  location.pathname.includes('/login') && "bg-blue-50 dark:bg-blue-500/10 scale-110"
                )}>
                  <LogIn size={22} strokeWidth={2} />
                </div>
                <span className="text-[10px] mt-1 font-medium opacity-70">登录</span>
              </>
            )}
          </button>
        </div>
      </nav>

    </>
  );
};

export default PublicMobileTabBar;
